
// import React, { useEffect, useRef, useState } from "react";

// /**
//  * MediaPicker
//  * - Accepts images and videos
//  * - Validates size/limits
//  * - Shows previews with remove buttons
//  * - Calls onFilesChange(files) whenever selection changes
//  */
// export default function MediaPicker({
//   onFilesChange,
//   multiple = true,
//   maxImageMB = 10,
//   maxVideoMB = 200,
//   maxFiles = 50,
//   label = "Add images/videos",
//   accept = "image/*,video/*",
// }) {
//   const inputRef = useRef(null);
//   const [mediaFiles, setMediaFiles] = useState([]); // File[]
//   const [previews, setPreviews] = useState([]);     // { url, type, name, size }[]

//   const openFileDialog = () => inputRef.current?.click();

//   const handleSelect = (e) => {
//     const files = Array.from(e.target.files || []);
//     if (!files.length) return;

//     const next = [];
//     const nextPreviews = [];

//     const currentCount = mediaFiles.length;
//     const availableSlots = Math.max(0, maxFiles - currentCount);
//     const limited = files.slice(0, availableSlots);

//     for (const f of limited) {
//       const isImage = f.type.startsWith("image/");
//       const isVideo = f.type.startsWith("video/");
//       if (!isImage && !isVideo) continue;

//       const sizeMB = f.size / (1024 * 1024);
//       if (isImage && sizeMB > maxImageMB) continue;
//       if (isVideo && sizeMB > maxVideoMB) continue;

//       next.push(f);
//       nextPreviews.push({
//         url: URL.createObjectURL(f),
//         type: isImage ? "image" : "video",
//         name: f.name,
//         size: f.size,
//       });
//     }

//     const newFiles = [...mediaFiles, ...next];
//     const newPreviews = [...previews, ...nextPreviews];
//     setMediaFiles(newFiles);
//     setPreviews(newPreviews);
//     onFilesChange?.(newFiles);
//   };

//   const removeAt = (i) => {
//     const p = previews[i];
//     if (p?.url) URL.revokeObjectURL(p.url);
//     const newPreviews = previews.filter((_, idx) => idx !== i);
//     const newFiles = mediaFiles.filter((_, idx) => idx !== i);
//     setPreviews(newPreviews);
//     setMediaFiles(newFiles);
//     onFilesChange?.(newFiles);
//   };

//   // cleanup object URLs on unmount
//   useEffect(() => {
//     return () => {
//       previews.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   return (
//     <div>
//       <button type="button" onClick={openFileDialog}>
//         {label}
//       </button>
//       <input
//         ref={inputRef}
//         type="file"
//         accept={accept}
//         multiple={multiple}
//         style={{ display: "none" }}
//         onChange={handleSelect}
//       />

//       {previews.length > 0 && (
//         <div
//           className="media-previews"
//           style={{
//             display: "grid",
//             gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
//             gap: 12,
//             marginTop: 12,
//           }}
//         >
//           {previews.map((m, i) => (
//             <div key={i} style={{ border: "1px solid #333", borderRadius: 8, padding: 8 }}>
//               {m.type === "image" ? (
//                 <img
//                   src={m.url}
//                   alt={m.name}
//                   style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 6 }}
//                 />
//               ) : (
//                 <video
//                   src={m.url}
//                   controls
//                   style={{ width: "100%", height: 120, borderRadius: 6 }}
//                 />
//               )}
//               <div
//                 style={{
//                   marginTop: 6,
//                   fontSize: 12,
//                   whiteSpace: "nowrap",
//                   overflow: "hidden",
//                   textOverflow: "ellipsis",
//                 }}
//                 title={m.name}
//               >
//                 {m.name}
//               </div>
//               <button
//                 type="button"
//                 onClick={() => removeAt(i)}
//                 style={{ marginTop: 6, width: "100%" }}
//               >
//                 Remove
//               </button>
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// }


import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";

/**
 * MediaPicker
 * - Accepts images and videos
 * - Validates size/limits
 * - Shows previews with remove buttons
 * - Calls onFilesChange(files) whenever selection changes
 * - Exposes ref.current.open() so parents (e.g., AppFooter button) can open the picker
 */
const MediaPicker = forwardRef(function MediaPicker(
  {
    onFilesChange,
    multiple = true,
    maxImageMB = 10,
    maxVideoMB = 200,
    maxFiles = 50,
    label = "Add images/videos",
    accept = "image/*,video/*",
  },
  ref
) {
  const inputRef = useRef(null);
  const [mediaFiles, setMediaFiles] = useState([]); // File[]
  const [previews, setPreviews] = useState([]);     // { url, type, name, size }[]

  const openFileDialog = () => inputRef.current?.click();

  useImperativeHandle(ref, () => ({
    open: openFileDialog,
    getFiles: () => mediaFiles,
    clear: () => {
      previews.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
      setPreviews([]);
      setMediaFiles([]);
      if (inputRef.current) inputRef.current.value = "";
      onFilesChange?.([]);
    },
  }));

  const handleSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const next = [];
    const nextPreviews = [];

    const currentCount = mediaFiles.length;
    const availableSlots = Math.max(0, maxFiles - currentCount);
    const limited = files.slice(0, availableSlots);

    for (const f of limited) {
      const isImage = f.type.startsWith("image/");
      const isVideo = f.type.startsWith("video/");
      if (!isImage && !isVideo) continue;

      const sizeMB = f.size / (1024 * 1024);
      if (isImage && sizeMB > maxImageMB) continue;
      if (isVideo && sizeMB > maxVideoMB) continue;

      next.push(f);
      nextPreviews.push({
        url: URL.createObjectURL(f),
        type: isImage ? "image" : "video",
        name: f.name,
        size: f.size,
      });
    }

    const newFiles = [...mediaFiles, ...next];
    const newPreviews = [...previews, ...nextPreviews];
    setMediaFiles(newFiles);
    setPreviews(newPreviews);
    onFilesChange?.(newFiles);
  };

  const removeAt = (i) => {
    const p = previews[i];
    if (p?.url) URL.revokeObjectURL(p.url);
    const newPreviews = previews.filter((_, idx) => idx !== i);
    const newFiles = mediaFiles.filter((_, idx) => idx !== i);
    setPreviews(newPreviews);
    setMediaFiles(newFiles);
    onFilesChange?.(newFiles);
  };

  // cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      previews.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {/* <button type="button" onClick={openFileDialog}>
        {label}
      </button> */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={handleSelect}
      />

      {previews.length > 0 && (
        <div
          className="media-previews"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 12,
            marginTop: 12,
          }}
        >
          {previews.map((m, i) => (
            <div key={i} style={{ border: "1px solid #333", borderRadius: 8, padding: 8 }}>
              {m.type === "image" ? (
                <img
                  src={m.url}
                  alt={m.name}
                  style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 6 }}
                />
              ) : (
                <video
                  src={m.url}
                  controls
                  style={{ width: "100%", height: 120, borderRadius: 6 }}
                />
              )}
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={m.name}
              >
                {m.name}
              </div>
              <button
                type="button"
                onClick={() => removeAt(i)}
                style={{ marginTop: 6, width: "100%" }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default MediaPicker;
